import { useEffect, useState } from "react";

/**
 * One shared ticker per interval length, rather than one per hook call.
 *
 * Every ProviderCard, the details modal, and the selection screen all want the same
 * clock. Given a timer each, they tick on independent phases: the card can drop a
 * lapsed slot up to a full interval before the screen clears the selection pointing
 * at it, leaving Confirm enabled on a time the list no longer offers. Sharing one
 * timer means every consumer in a render pass sees the SAME instant, so the filtered
 * list and the selection derived from it can never disagree.
 */
const tickers = new Map<
  number,
  {
    id: ReturnType<typeof setInterval>;
    subscribers: Set<(now: number) => void>;
  }
>();

const subscribe = (
  intervalMs: number,
  onTick: (now: number) => void,
): (() => void) => {
  let ticker = tickers.get(intervalMs);

  if (!ticker) {
    const subscribers = new Set<(now: number) => void>();
    const id = setInterval(() => {
      const now = Date.now();
      subscribers.forEach((notify) => notify(now));
    }, intervalMs);

    ticker = { id, subscribers };
    tickers.set(intervalMs, ticker);
  }

  ticker.subscribers.add(onTick);

  return () => {
    const current = tickers.get(intervalMs);

    if (!current) return;

    current.subscribers.delete(onTick);

    // Last one out stops the timer, so an unmounted tree leaves nothing running.
    if (current.subscribers.size === 0) {
      clearInterval(current.id);
      tickers.delete(intervalMs);
    }
  };
};

/**
 * A clock that re-renders its consumer on a fixed interval.
 *
 * Time-dependent UI — here, hiding appointment slots once their start time has
 * passed — only updates when React re-renders, and nothing re-renders a page the
 * patient is simply sitting on. Without a tick, a slot list loaded at 1:55 keeps
 * offering the 2:00 slot indefinitely.
 *
 * Deliberately NOT solved by refetching availability on an interval: a background
 * refetch remounts the provider cards mid-interaction and swallows slot clicks
 * (see the query options in ProviderSelectionScreen). This is a local clock, no
 * network involved.
 *
 * @param intervalMs how often to re-render; one minute is enough for slot times
 * @returns the current epoch milliseconds, refreshed each interval
 */
export function useNow(intervalMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => subscribe(intervalMs, setNow), [intervalMs]);

  return now;
}
